export type TUserAgentInfo = {
	/** "Chrome", "Safari"... null when it cannot be told. */
	browser: string | null;
	/** "macOS", "iPhone"... null when it cannot be told. */
	system: string | null;
	/** The kind of device, named as its picto: the list shows it. */
	device: TDeviceKind;
};

export type TDeviceKind = "smartphone" | "tablet" | "monitor";

// Order matters: Edge and Opera say "Chrome" too, Chrome says "Safari",
// and every iOS browser is Safari underneath (CriOS/FxiOS name the real one).
const BROWSERS: [RegExp, string][] = [
	[/Edg(e|A|iOS)?\//, "Edge"],
	[/OPR\/|Opera/, "Opera"],
	[/Firefox\/|FxiOS\//, "Firefox"],
	[/SamsungBrowser\//, "Samsung Internet"],
	[/Chrome\/|CriOS\//, "Chrome"],
	[/Safari\//, "Safari"],
];

const SYSTEMS: [RegExp, string][] = [
	[/iPhone/, "iPhone"],
	[/iPad/, "iPad"],
	[/Android/, "Android"],
	[/Windows/, "Windows"],
	[/Mac OS X|Macintosh/, "macOS"],
	[/CrOS/, "ChromeOS"],
	[/Linux/, "Linux"],
];

const first = (ua: string, table: [RegExp, string][]): string | null =>
	table.find(([pattern]) => pattern.test(ua))?.[1] ?? null;

/**
 * What the security page can say about a device from its User-Agent: the
 * browser and the system, the way a person recognises their own devices
 * ("Chrome · macOS"). A heuristic on purpose - User-Agents are free text, a
 * parsing library would not make the guess any surer for this use.
 */
export const describeUserAgent = (ua?: string | null): TUserAgentInfo => {
	if (!ua) return { browser: null, system: null, device: "monitor" };
	const system = first(ua, SYSTEMS);
	// An Android tablet drops the "Mobile" its phones carry.
	const tablet =
		system === "iPad" || (system === "Android" && !/Mobile/.test(ua));
	const phone = system === "iPhone" || /Mobile/.test(ua);
	return {
		browser: first(ua, BROWSERS),
		system,
		device: tablet ? "tablet" : phone ? "smartphone" : "monitor",
	};
};
